import React from 'react';
import { StyledBodyText } from '../../../../../../common/Styleguide/Common/Text';
import { styled } from '@mui/system';
import { COLORS } from '../../../../../../common/Styleguide/Common/colors';
import { Tooltip } from '@mui/material';
import { Submission } from '../submission-graphql';

const FlowTypeRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '16px',
});

const FlowTypeItem = styled('div')(({ active = false }: { active?: boolean }) => ({
  flex: '1 1 0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '12px 16px',
  borderRadius: '8px',
  border: `1px solid ${COLORS.black[50]}`,
  backgroundColor: active ? COLORS.brand[100] : COLORS.black[80],
  cursor: 'default',
}));

interface FlowTypeProps {
  submission?: Submission;
  isFullLaunch?: boolean;
}

/**
 * For normal submissions renders 2 boxes: Basic and Full, and highlights the active one.
 * For Game Jam submissions renders a single box with the text "Game Jam".
 */
const FlowType: React.FC<FlowTypeProps> = ({ submission, isFullLaunch }) => {
  const currentFlow = () => {
    if (submission?.gameJamId) {
      return 'gameJam';
    }
    if (submission?.isFullLaunch || (!submission && isFullLaunch)) {
      return 'full';
    }
    return 'basic';
  };

  const flow = currentFlow();

  const renderNormalFlow = () => (
    <>
      <Tooltip title="Your game will be released for a subset of our users. After a test period, if the KPIs are met, the game will be transitioned to Full Launch. Read more about the process in our documentation.">
        <FlowTypeItem active={flow === 'basic'}>
          <StyledBodyText variant="h3" color={flow === 'basic' ? 'white100' : 'white80'} sx={{ margin: 0 }}>
            Basic
          </StyledBodyText>
        </FlowTypeItem>
      </Tooltip>
      <Tooltip title="Your game will be released for all CrazyGames users. Read more about the process in our documentation.">
        <FlowTypeItem active={flow === 'full'}>
          <StyledBodyText variant="h3" color={flow === 'full' ? 'white100' : 'white80'} sx={{ margin: 0 }}>
            Full
          </StyledBodyText>
        </FlowTypeItem>
      </Tooltip>
    </>
  );

  const renderGameJamFlow = () => (
    <Tooltip title="Your game was submitted as a Game Jam entry.">
      <FlowTypeItem active>
        <StyledBodyText variant="h3" color={'white100'} sx={{ margin: 0 }}>
          Game Jam
        </StyledBodyText>
      </FlowTypeItem>
    </Tooltip>
  );

  return <FlowTypeRow>{flow === 'gameJam' ? renderGameJamFlow() : renderNormalFlow()}</FlowTypeRow>;
};

export default FlowType;
